import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';

const TENDENZA = {
  'in aumento': 'bg-red-100 text-red-700',
  'in calo': 'bg-emerald-100 text-emerald-700',
  stabile: 'bg-slate-100 text-slate-600',
};
const asseX = { dataKey: 'etichetta', tick: { fontSize: 11, fill: '#64748b' }, interval: 0, angle: -35, textAnchor: 'end', height: 48 };
const asseY = { tick: { fontSize: 11, fill: '#64748b' }, allowDecimals: false };

const Grafico = ({ titolo, tendenza, children, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-1">
      <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
        {titolo}
        {tendenza && <Badge className={`text-[10px] ${TENDENZA[tendenza] || TENDENZA.stabile}`}>{tendenza}</Badge>}
      </CardTitle>
    </CardHeader>
    <CardContent className="h-56 pt-2">
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </CardContent>
  </Card>
);

export default function AndamentoQualita() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get('/qualita/andamento', { params: { mesi: 12 } }).then(r => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return null;

  return (
    <div className="space-y-4" data-testid="andamento-qualita">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-brand" />
        <h2 className="text-base md:text-lg font-semibold text-slate-800">Andamento degli ultimi 12 mesi</h2>
        <span className="text-xs text-slate-400">la tendenza confronta la media dei primi 6 mesi con quella degli ultimi 6</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Grafico titolo="Cadute per mese" tendenza={d.tendenze.cadute} testid="grafico-cadute">
          <BarChart data={d.serie}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis {...asseX} /><YAxis {...asseY} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="cadute" name="Cadute" fill="#0f766e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="cadute_gravi" name="Con esito grave" fill="#dc2626" radius={[4, 4, 0, 0]} />
          </BarChart>
        </Grafico>
        <Grafico titolo="Lesioni da pressione" tendenza={d.tendenze.lesioni_in_struttura} testid="grafico-lesioni">
          <LineChart data={d.serie}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis {...asseX} /><YAxis {...asseY} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Line type="monotone" dataKey="lesioni_aperte" name="Aperte a fine mese" stroke="#0284c7" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="lesioni_in_struttura" name="Insorte in struttura" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </Grafico>
        <Grafico titolo="Contenzioni attive" tendenza={d.tendenze.contenzioni_attive} testid="grafico-contenzioni">
          <LineChart data={d.serie}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis {...asseX} /><YAxis {...asseY} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Line type="monotone" dataKey="contenzioni_attive" name="Contenzioni attive" stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </Grafico>
        <Grafico titolo="Valutazioni con scale e somministrazioni mancate" tendenza={d.tendenze.valutazioni_scale} testid="grafico-scale">
          <BarChart data={d.serie}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis {...asseX} /><YAxis {...asseY} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="valutazioni_scale" name="Scale compilate" fill="#0f766e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="somministrazioni_mancate" name="Somministrazioni mancate" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </Grafico>
      </div>
    </div>
  );
}
